import { useEffect, useMemo, useState } from 'react'
import EnvironmentReportTable from '../components/EnvironmentTables/EnvironmentReportTable'
import { useFilterOnConsolidateAgreement } from '../../hooks/useFilterOnConsolidateAgreement'
import { useQuery } from 'react-query'
import { fetchEnvironmentReport } from '../../../../api/api'
import { useChosenCompany } from '../../../../context/UserProvider'
import { Select } from '../../../cars/components/V3/_shared/Select'
import { ConsolidateInvoiceSelect } from '../../../cars/components/V3/_shared/ConsolidateInvoiceSelect'
import { ExcelExportButton } from '../components/ExcelExporter'
import { excelEnvironmentReportColumns } from '../components/EnvironmentTables/excelUtils'
import StatsTable from '../components/EnvironmentTables/StatsTable'
import type { Environment } from '../../../../types'

const EnvironmentReport = () => {
  const company = useChosenCompany()
  const companyId = company?.id

  const [year, yearSet] = useState('')

  const { data: response, status } = useQuery(
    ['environmentReport', companyId],
    () => fetchEnvironmentReport(companyId),
    {
      enabled: !!companyId,
    },
  )

  const data = (
    Array.isArray(response) ? response : response && 'data' in response ? response.data : []
  ) as Environment[]

  const reportYears = useMemo(() => {
    return Array.from(new Set(data.map((el) => el.year))).sort((a, b) => b - a)
  }, [data])

  useEffect(() => {
    if (reportYears.length > 0 && year === '') {
      yearSet(reportYears[0].toString())
    }
  }, [reportYears, year])

  const items = [
    { value: 'all', label: 'Alle' },
    ...reportYears.map((year) => ({ value: String(year), label: String(year) })),
  ]

  const agreementFilter = useFilterOnConsolidateAgreement()
  const filteredData = data
    .filter((el) => year === 'all' || String(el.year) === year)
    .filter(agreementFilter('consolidatedInvoiceAgreement'))

  const averageCo2PerCar =
    filteredData.length > 0
      ? filteredData.reduce((total, current) => total + current.co2Emission, 0) / filteredData.length
      : 0

  const averageNoxPerCar =
    filteredData.length > 0
      ? filteredData.reduce((total, current) => total + current.nOXEmission, 0) / filteredData.length
      : 0

  const statsData = [{ averageCo2PerCar, averageNoxPerCar }]

  const columns = excelEnvironmentReportColumns()
  const config = { columns, filename: `Miljørapport_${year}.xlsx`, sheetName: `Miljørapport_${year}` }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:justify-between">
        <div className="flex flex-row justify-between items-center">
          <span className="text-xl font-medium text-primaryText">Miljørapport</span>
          <div className="md:hidden">
            <ExcelExportButton data={filteredData} config={config} />
          </div>
        </div>
        <div className="flex flex-row gap-2 w-full md:w-auto">
          <div className="flex-1 md:flex-none">
            <Select className="h-11 w-full" placeholder={'Periode'} options={items} value={year} onChange={yearSet} />
          </div>
          <div className="flex-1 md:flex-none">
            <ConsolidateInvoiceSelect className="h-11 w-full" />
          </div>
          <div className="hidden md:block">
            <ExcelExportButton data={filteredData} config={config} />
          </div>
        </div>
      </div>

      <EnvironmentReportTable isLoading={status === 'loading'} data={filteredData} />
      <StatsTable data={statsData} />
    </div>
  )
}

export default EnvironmentReport
